import {
  WorkflowTaskConfiguration,
  WorkflowTrigger,
} from '@qtek/shared/models';
import { addDays, addMonths, endOfMonth } from 'date-fns';

/**
 * Workflow Constants
 *
 * This object contains all the magic strings and constants used throughout the workflow components.
 * Having them centralized here ensures consistency and makes updates easier.
 */
export const WORKFLOW_CONSTANTS = {
  /**
   * Prefix used for reference IDs in workflow operations.
   * Format: 'itm={refId}'
   */
  REF_ID_PREFIX: 'itm=',

  /**
   * Schema ID for the next payment date model
   */
  SCHEMA_ID: 'io.synply.v1.model.core.agency.nPmtDt',

  /**
   * Document attributes constant used in workflow payloads
   */
  DOCUMENT_ATTRS: 6020,

  /**
   * Minimum length for workflow name validation
   */
  MIN_NAME_LENGTH: 3,
} as const;

/**
 * Helper function to format a reference ID with the standard prefix
 * @param refId - The reference ID to format
 * @returns The formatted reference ID (e.g., 'itm=123')
 */
export const formatRefId = (refId: string): string =>
  refId.startsWith(WORKFLOW_CONSTANTS.REF_ID_PREFIX)
    ? refId
    : `${WORKFLOW_CONSTANTS.REF_ID_PREFIX}${refId}`;

/**
 * Helper function to extract the reference ID from a formatted string
 * @param formattedRefId - The formatted reference ID (e.g., 'itm=123')
 * @returns The extracted reference ID (e.g., '123')
 */
export const extractRefId = (formattedRefId: string): string =>
  formattedRefId.replace(WORKFLOW_CONSTANTS.REF_ID_PREFIX, '');

/**
 * Mapping of workflow frequency values to translation keys
 */
export const WORKFLOW_FREQUENCY_MAP: Record<string, string> = {
  once: 'GNR_ONCE',
  day: 'GNR_DAILY',
  week: 'GNR_WEEKLY',
  month: 'GNR_MONTHLY',
  quarter: 'GNR_QUARTERLY',
};

/**
 * Mapping of workflow trigger types to translation keys
 */
export const WORKFLOW_TRIGGER_TYPE_MAP: Record<string, string> = {
  schedule: 'GNR_SCHEDULE',
  attribute: 'GNR_ATTRIBUTE',
  deal_status: 'GNR_DEAL_STATUS_CHANGED',
};

/**
 * Safely extracts schedule data from the first trigger in the triggers array
 * @param triggers - Array of workflow triggers
 * @returns Translation key for the schedule frequency, or empty string if not available
 */
export function getScheduleFromFirstTrigger(
  triggers?: WorkflowTrigger[]
): string {
  if (!Array.isArray(triggers) || triggers.length === 0) {
    return '';
  }

  const firstTrigger = triggers[0];

  if (firstTrigger.type === 'schedule' && firstTrigger.scheduleFrequency) {
    const frequency = firstTrigger.scheduleFrequency.frequency;

    return WORKFLOW_FREQUENCY_MAP[frequency] || frequency;
  }

  return '';
}

/**
 * Safely extracts trigger type from the first trigger in the triggers array
 * @param triggers - Array of workflow triggers
 * @returns Translation key for the trigger type, or 'GNR_UNDEFINED' if not available
 */
export function getTriggerTypeFromFirstTrigger(
  triggers?: WorkflowTrigger[]
): string {
  if (!Array.isArray(triggers) || triggers.length === 0) {
    return 'GNR_UNDEFINED';
  }

  const firstTrigger = triggers[0];

  return WORKFLOW_TRIGGER_TYPE_MAP[firstTrigger.type] || firstTrigger.type;
}

/**
 * Gets the frequency label from a frequency value
 * @param frequency - The frequency value (once, day, week, month, quarter)
 * @returns Translation key for the frequency
 */
export function getFrequencyLabel(frequency: string): string {
  return WORKFLOW_FREQUENCY_MAP[frequency] || frequency;
}

/**
 * Gets the trigger type label from a trigger type value
 * @param triggerType - The trigger type (schedule, attribute)
 * @returns Translation key for the trigger type
 */
export function getTriggerTypeLabel(triggerType: string): string {
  return WORKFLOW_TRIGGER_TYPE_MAP[triggerType] || triggerType;
}

/**
 * Frequency options for workflow schedules
 * Labels use translation keys from WORKFLOW_FREQUENCY_MAP
 */
export const WORKFLOW_FREQUENCY_OPTIONS = [
  { label: 'GNR_ONCE', value: 'once' },
  { label: 'GNR_DAILY', value: 'day' },
  { label: 'GNR_WEEKLY', value: 'week' },
  { label: 'GNR_MONTHLY', value: 'month' },
  { label: 'GNR_QUARTERLY', value: 'quarter' },
] as const;

/**
 * Computes a due date timestamp from a trigger timestamp and a workflow task configuration.
 *
 * Supported dueDateType values:
 *  - 'days'               — adds `dueDateOffset` days to the trigger date
 *  - 'months'             — adds `dueDateOffset` months to the trigger date
 *  - 'quarters'           — adds `dueDateOffset * 3` months to the trigger date
 *  - 'atTheEndOfTheMonth' — returns the last moment of the trigger's month
 *
 * @param triggerTs - Trigger event timestamp in milliseconds
 * @param config    - Workflow task configuration containing `dueDateType` and `dueDateOffset`
 * @returns Due date as a Unix timestamp (ms), or `triggerTs` if no type matched, or `0` if inputs are missing
 */
export function computeDueDate(
  triggerTs: number,
  config: WorkflowTaskConfiguration | undefined
): number {
  if (!config || !triggerTs) return 0;

  const dueDateType = config.params?.['dueDateType']?.[0];
  const offset = Number.parseInt(
    config.params?.['dueDateOffset']?.[0] ?? '0',
    10
  );
  const base = new Date(triggerTs);

  if (dueDateType === 'days') {
    return addDays(base, offset).getTime();
  }
  if (dueDateType === 'months') {
    return addMonths(base, offset).getTime();
  }
  if (dueDateType === 'quarters') {
    return addMonths(base, offset * 3).getTime();
  }
  if (dueDateType === 'atTheEndOfTheMonth') {
    return endOfMonth(base).getTime();
  }

  return triggerTs;
}
